import React, { useState, useEffect, useCallback } from 'react';
import { Page } from '../types';
import { getArticles, getGalleries, getCampaigns, slugify, getMediaUrl } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import CustomCalendar from '../components/CustomCalendar';
import { Link } from 'react-router-dom';
import { formatDate, formatTime } from '../utils/formatDate';
import { formatCategoryName } from '../utils/formatCategory';

type CategoryKey = 'all' | 'press' | 'campaigns' | 'events' | 'gallery' | 'culture' | 'brochures';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: Exclude<CategoryKey, 'all'>;
  date: string | null;
  time?: string | null;
  rawDate: string | null;
  imageUrl: string;
  location?: string;
  slug: string; // Added slug
}



interface BasinKulturSanatProps {
  currentPage: Page;
}

const BasinKulturSanat: React.FC<BasinKulturSanatProps> = ({ currentPage = 'basin' }) => {
  const { t } = useTranslation();
  const { locale } = useLanguage();

  
  // Set initial tab based on the current page identifier
  const getInitialTab = useCallback((): CategoryKey => {
    switch (currentPage) {
      case 'basin-bultenleri': return 'press';
      case 'etkinlikler': return 'events';
      case 'galeri': return 'gallery';
      case 'kampanya':
      case 'kampanyalar': return 'campaigns';
      case 'basin': return 'all';
      default: return 'all';
    }
  }, [currentPage]);

  const [activeTab, setActiveTab] = useState<CategoryKey>(getInitialTab());
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 12; // 4 columns * 3 rows

  // Gallery states
  const [galleryCategories, setGalleryCategories] = useState<string[]>([]);
  const [selectedGalleryCategory, setSelectedGalleryCategory] = useState<string>('all');
  const [galleries, setGalleries] = useState<any[]>([]);
  const [selectedGallery, setSelectedGallery] = useState<any | null>(null);
  const [selectedAlbum, setSelectedAlbum] = useState<any | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);

  // Update tab when page changes (e.g. via navigation)
  useEffect(() => {
    setActiveTab(getInitialTab());
  }, [currentPage, getInitialTab]);

  // Reset page and clear data when tab or locale changes
  useEffect(() => {
    setPage(1);
    setArticles([]);
    setGalleries([]);
    setSelectedDate(null);
    setSelectedGalleryCategory('all');
    setSelectedAlbum(null);
  }, [activeTab, locale]);

  const handleSelectGalleryCategory = (catName: string) => {
    if (selectedGalleryCategory === catName) {
      if (selectedAlbum) {
        setSelectedAlbum(null);
      }
      return;
    }
    setSelectedGalleryCategory(catName);
    setPage(1);
    setSelectedAlbum(null);
  };

  // Fetch gallery categories (extract unique category strings from all galleries)
  useEffect(() => {
    let ignore = false;
    if (activeTab === 'gallery') {
      const fetchCats = async () => {
        try {
          const { data } = await getGalleries(locale, 'all', 1, 1000);
          if (ignore) return;
          const uniqueCats = Array.from(new Set(data.map((item: any) => item.category || item.attributes?.category).filter(Boolean))) as string[];
          setGalleryCategories(uniqueCats);
        } catch (e) {
          console.error("Failed to load gallery categories", e);
        }
      };
      fetchCats();
    }
    return () => {
      ignore = true;
    };
  }, [activeTab, locale]);

  useEffect(() => {
    let ignore = false;

    const fetchData = async () => {
      if (page === 1) setLoading(true);
      try {
        if (activeTab === 'gallery') {
          const { data, meta } = await getGalleries(locale, selectedGalleryCategory, page, pageSize);
          if (ignore) return;

          const formattedGalleries = data.map((item: any) => ({
            id: item.documentId || item.id,
            title: item.title,
            images: (item.images || []).map((img: any) => ({
              id: img.id,
              url: img.url
            })),
            publishedAt: item.publishedAt,
            createdAt: item.createdAt,
            category: item.category
          }));

          if (page === 1) {
            setGalleries(formattedGalleries);
          } else {
            setGalleries(prev => [...prev, ...formattedGalleries]);
          }

          if (meta && meta.pagination) {
            setHasMore(page < meta.pagination.pageCount);
          } else {
            setHasMore(false);
          }
        } else {
          // Map frontend tab to Strapi category
          let data: any[] = [];
          let meta: any = null;

          if (activeTab === 'campaigns') {
            const [campRes, artRes] = await Promise.all([
              getCampaigns(undefined, locale, page, pageSize),
              getArticles(undefined, locale, page, pageSize, 'Kampanya')
            ]);
            const campList = (campRes.data || []).map((c: any) => ({ ...c, category: 'Kampanya' }));
            const artList = artRes.data || [];
            const combined = [...campList, ...artList];
            const seen = new Set();
            const uniqueData = combined.filter((item: any) => {
              const key = item.documentId || item.id;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            uniqueData.sort((a: any, b: any) => {
              const dateA = new Date(a.publishedDate || a.publishedAt || a.createdAt || 0).getTime();
              const dateB = new Date(b.publishedDate || b.publishedAt || b.createdAt || 0).getTime();
              return dateB - dateA;
            });
            data = uniqueData;
            meta = campRes.meta || artRes.meta;
          } else {
            let categoryParam: string | undefined = undefined;
            if (activeTab === 'press') categoryParam = 'Basin';
            if (activeTab === 'culture') categoryParam = 'Kultur';
            if (activeTab === 'events') categoryParam = 'Etkinlikler';
            if (activeTab === 'brochures') categoryParam = 'Brosur';

            const res = await getArticles(undefined, locale, page, pageSize, categoryParam);
            data = res.data;
            meta = res.meta;
          }
          if (ignore) return;
          
          const formattedArticles: Article[] = data.map((item: any) => {
            const catVal = item?.category;
            let category: any = catVal || 'culture';

            const imageUrl = item.coverImage?.url
              ? getMediaUrl(item.coverImage.url)
              : 'https://images.unsplash.com/photo-1492037766660-2a56f9eb3fcb?q=80&w=800&auto=format&fit=crop';

            const excerpt = item.content?.[0]?.children?.[0]?.text || '';

            const isEvent = catVal === 'Etkinlikler' || activeTab === 'events';
            const effectiveDate = (isEvent && item.eventDate) ? item.eventDate : item.publishedDate;
            const eventTime = (isEvent && item.eventDate) ? formatTime(item.eventDate, locale) : null;
            const location = item.eventVenue || item.location || undefined;

            return {
              id: item.documentId || item.id,
              title: item.title,
              excerpt: excerpt,
              category: category,
              date: formatDate(effectiveDate, locale),
              time: eventTime,
              rawDate: effectiveDate ? new Date(effectiveDate).toISOString().split('T')[0] : null,
              imageUrl: imageUrl,
              location: location,
              slug: item.slug || slugify(item.title) || item.documentId || item.id,
            };
          });

          if (page === 1) {
            setArticles(formattedArticles);
          } else {
            setArticles(prev => [...prev, ...formattedArticles]);
          }
          
          if (meta && meta.pagination) {
            setHasMore(page < meta.pagination.pageCount);
          } else {
            setHasMore(false);
          }
        }
      } catch (error) {
        console.error("Failed to load content", error);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchData();

    return () => {
      ignore = true;
    };
  }, [locale, activeTab, selectedGalleryCategory, page]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const filteredArticles = articles; // Server-side filtering applied

  const eventDates = articles.map(a => a.rawDate).filter(Boolean) as string[];

  const calendarFilteredArticles = selectedDate 
    ? articles.filter(a => a.rawDate === selectedDate.toISOString().split('T')[0])
    : articles;

  const categories: CategoryKey[] = ['all', 'press', 'campaigns', 'events', 'gallery', 'culture', 'brochures'];



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-neutral-bg">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-brand-purple-900"></div>
      </div>
    );
  }

  return (
    <div className="py-24 bg-brand-neutral-bg min-h-screen animate-in fade-in duration-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 space-y-6">
          <div className="inline-flex items-center space-x-2 text-brand-purple-500 font-bold text-xs uppercase tracking-widest">
            <span className="w-8 h-px bg-brand-purple-500"></span>
            <span>{t('news_page.subtitle')}</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-brand-purple-900 leading-tight">
            {t('news_page.title') || t('nav.news')}
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto font-light leading-relaxed">
            {t('news_page.desc') || t('home.hero_subtitle')}
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-16 border-b border-brand-purple-100/20 pb-10">
          {categories.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                if (activeTab === tab) return;
                setActiveTab(tab);
              }}
              className={`px-8 py-3 rounded-2xl text-sm font-bold transition-all duration-300 ${activeTab === tab
                ? 'bg-brand-purple-900 text-white shadow-xl shadow-brand-purple-900/20 scale-105'
                : 'bg-white text-gray-500 border border-brand-purple-50 hover:border-brand-purple-200 hover:text-brand-purple-900 shadow-sm'
                }`}
            >
              {t(`news_page.cat_${tab}`)}
            </button>
          ))}
        </div>

        {/* Content Grid */}
        {activeTab === 'events' ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="lg:col-span-4">
              <CustomCalendar 
                selectedDate={selectedDate} 
                onDateSelect={setSelectedDate} 
                eventDates={eventDates} 
              />
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {calendarFilteredArticles.length > 0 ? (
                  calendarFilteredArticles.map((article: any) => (
                    <Link
                      key={article.id}
                      className="group flex flex-col bg-white rounded-[2rem] overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-brand-purple-900/10 border border-brand-purple-50/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                      to={`/${locale}/haberler/${article.slug || article.id}`}
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                        <div className="absolute top-4 right-4">
                          <span className="bg-brand-purple-900/90 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-lg">{t('news_page.cat_events')}</span>
                        </div>
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <span className="text-brand-green-500 font-bold text-xs uppercase tracking-widest flex items-center flex-wrap gap-1.5">
                            <span className="flex items-center">
                              <svg className="w-3.5 h-3.5 mr-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                              {article.date}
                            </span>
                            {article.time && (
                              <span className="flex items-center text-brand-purple-900 font-extrabold bg-brand-purple-50 border border-brand-purple-100/50 px-2 py-0.5 rounded-md text-[11px]">
                                <svg className="w-3 h-3 mr-1 shrink-0 text-brand-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                {article.time}
                              </span>
                            )}
                          </span>
                          {article.location && (
                            <span className="text-[10px] text-gray-500 font-bold uppercase flex items-center bg-gray-100 px-2 py-0.5 rounded-md truncate max-w-[45%]">
                              <svg className="w-3 h-3 mr-1 shrink-0 text-brand-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                              <span className="truncate">{article.location}</span>
                            </span>
                          )}
                        </div>
                        <h3 className="text-xl font-serif font-bold text-brand-purple-900 mb-2 leading-tight group-hover:text-brand-purple-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 font-light">
                          {article.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="col-span-full text-center py-20 bg-white rounded-[2rem] border border-brand-purple-50">
                    <p className="text-lg text-gray-400 font-light">{t('news_page.no_events_date')}</p>
                    <button onClick={() => setSelectedDate(null)} className="mt-4 text-brand-purple-900 font-bold hover:underline">{t('news_page.show_all')}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeTab === 'gallery' ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Left Category Sidebar */}
            <div className="lg:col-span-3 space-y-4">
              <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-brand-purple-50/50">
                <h3 className="text-lg font-serif font-bold text-brand-purple-900 mb-4 pb-2 border-b border-brand-purple-100/50">
                  {t('gallery.categories_title') || 'Galeri Kategorileri'}
                </h3>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleSelectGalleryCategory('all')}
                    className={`text-left px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                      selectedGalleryCategory === 'all'
                        ? 'bg-brand-purple-900 text-white shadow-md'
                        : 'text-gray-600 hover:bg-brand-purple-50 hover:text-brand-purple-900'
                    }`}
                  >
                    {t('news_page.cat_all') || 'Tümü'}
                  </button>
                  {galleryCategories.map((catName: string) => (
                    <button
                      key={catName}
                      onClick={() => handleSelectGalleryCategory(catName)}
                      className={`text-left px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                        selectedGalleryCategory === catName
                          ? 'bg-brand-purple-900 text-white shadow-md'
                          : 'text-gray-600 hover:bg-brand-purple-50 hover:text-brand-purple-900'
                      }`}
                    >
                      {catName}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Galleries Grid / Album Detail View */}
            <div className="lg:col-span-9 animate-in fade-in duration-300">
              {selectedAlbum ? (
                <div className="space-y-6">
                  {/* Album Detail Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-brand-purple-50/50">
                    <button
                      onClick={() => setSelectedAlbum(null)}
                      className="flex items-center text-brand-purple-900 font-bold hover:text-brand-purple-600 transition-all active:scale-95 text-xs uppercase tracking-widest bg-brand-purple-50 hover:bg-brand-purple-100/75 px-5 py-2.5 rounded-full"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      {t('gallery.back_to_albums') || 'Tüm Albümlere Dön'}
                    </button>
                    <h3 className="text-2xl font-serif font-bold text-brand-purple-900 leading-tight">
                      {selectedAlbum.title}
                    </h3>
                  </div>

                  {/* Single Album Photo Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {selectedAlbum.images && selectedAlbum.images.length > 0 ? (
                      selectedAlbum.images.map((img: any, index: number) => {
                        const imgUrl = img.url 
                          ? getMediaUrl(img.url)
                          : 'https://images.unsplash.com/photo-1492037766660-2a56f9eb3fcb?q=80&w=800&auto=format&fit=crop';
                        
                        return (
                          <div
                            key={img.id || index}
                            onClick={() => {
                              setSelectedGallery(selectedAlbum);
                              setLightboxIndex(index);
                            }}
                            className="group relative aspect-square bg-white rounded-[1.5rem] overflow-hidden shadow-sm hover:shadow-xl border border-brand-purple-50/50 transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                          >
                            <img
                              src={imgUrl}
                              alt={`${selectedAlbum.title} - ${index + 1}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-brand-purple-900/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        );
                      })
                    ) : (
                      <div className="col-span-full text-center py-20 bg-white rounded-[2rem] border border-brand-purple-50">
                        <p className="text-lg text-gray-400 font-light">{t('gallery.empty_album')}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {galleries.length > 0 ? (
                    galleries.map((gal: any) => {
                      const coverImg = gal.images?.[0]?.url 
                        ? getMediaUrl(gal.images[0].url)
                        : 'https://images.unsplash.com/photo-1492037766660-2a56f9eb3fcb?q=80&w=800&auto=format&fit=crop';
                      
                      return (
                        <div
                          key={gal.id}
                          onClick={() => {
                            setSelectedAlbum(gal);
                          }}
                          className="group relative aspect-[4/5] bg-white rounded-[2rem] overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-brand-purple-900/10 border border-brand-purple-50/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                        >
                          <img
                            src={coverImg}
                            alt={gal.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-brand-purple-900/95 via-brand-purple-900/40 to-transparent flex flex-col justify-end p-6 text-white">
                            <h4 className="font-serif font-bold text-lg mb-2 line-clamp-2">{gal.title}</h4>
                            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest opacity-80 mt-1">
                              <span>{gal.images?.length || 0} {t('gallery.photos')}</span>
                              <span>{formatDate(gal.publishedAt || gal.createdAt, locale)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-span-full text-center py-20 bg-white rounded-[2rem] border border-brand-purple-50">
                      <p className="text-lg text-gray-400 font-light">{t('gallery.empty_gallery')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-8">
            {filteredArticles.length > 0 ? (
              filteredArticles.map((article: any) => (
                <Link
                  key={article.id}
                  className="group flex flex-col justify-between h-full bg-white rounded-[2rem] overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-brand-purple-900/10 border border-brand-purple-50/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer min-h-[400px]"
                  to={`/${locale}/haberler/${article.slug || article.id}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                    />
                    <div className="absolute top-6 left-6">
                      <span className="bg-brand-purple-900/90 backdrop-blur-md text-white text-[10px] font-bold px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg">
                        {formatCategoryName(article.category, t)}
                      </span>
                    </div>
                  </div>

                  {/* Text Info */}
                  <div className="p-8 flex flex-col flex-1 justify-between">
                    <div className="flex-1 flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-brand-green-500 font-bold text-xs uppercase tracking-widest">{article.date}</span>
                        {article.location && (
                          <span className="text-[10px] text-gray-400 font-bold uppercase flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                            {article.location}
                          </span>
                        )}
                      </div>
                      <h3 className="text-2xl font-serif font-bold text-brand-purple-900 mb-4 leading-tight group-hover:text-brand-purple-600 transition-colors line-clamp-3">
                        {article.title}
                      </h3>
                      <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-8 font-light">
                        {article.excerpt}
                      </p>
                    </div>
                    <div className="pt-6 border-t border-gray-100 mt-auto">
                      <button className="text-brand-purple-900 font-bold text-xs uppercase tracking-widest flex items-center group-hover:text-brand-green-500 transition-colors">
                        {t('news_page.read_more')}
                        <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                      </button>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="col-span-full text-center py-20">
                <p className="text-xl text-gray-400 font-light">{t('news_page.empty')}</p>
              </div>
            )}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && !(activeTab === 'gallery' && selectedAlbum) && (
          <div className="mt-16 flex justify-center">
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={loading}
              className="bg-white border-2 border-brand-purple-200 text-brand-purple-900 font-bold px-10 py-4 rounded-2xl shadow-sm hover:shadow-lg hover:border-brand-purple-500 hover:text-brand-purple-600 transition-all active:scale-95 disabled:opacity-50"
            >
              {loading ? t('news_page.loading') : t('resources.load_more')}
            </button>
          </div>
        )}
      </div>

      {/* Lightbox / Photo Viewer Modal */}
      {selectedGallery && selectedGallery.images && selectedGallery.images.length > 0 && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 backdrop-blur-md transition-opacity duration-300 animate-in fade-in">
          {/* Close Button */}
          <button
            onClick={() => setSelectedGallery(null)}
            className="absolute top-6 right-6 text-white/85 hover:text-white transition-all bg-white/10 hover:bg-white/20 p-3 rounded-full hover:scale-105 active:scale-95 duration-200"
            aria-label="Kapat"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Left Arrow */}
          {selectedGallery.images.length > 1 && (
            <button
              onClick={() => setLightboxIndex(prev => (prev === 0 ? selectedGallery.images.length - 1 : prev - 1))}
              className="absolute left-6 text-white/85 hover:text-white transition-all bg-white/10 hover:bg-white/20 p-4 rounded-full hover:scale-105 active:scale-95 duration-200"
              aria-label="Önceki Fotoğraf"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Center Image */}
          <div className="max-w-5xl max-h-[80vh] px-4 flex items-center justify-center">
            <img
              src={getMediaUrl(selectedGallery.images[lightboxIndex].url)}
              alt={`Fotoğraf ${lightboxIndex + 1}`}
              className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300"
            />
          </div>

          {/* Right Arrow */}
          {selectedGallery.images.length > 1 && (
            <button
              onClick={() => setLightboxIndex(prev => (prev === selectedGallery.images.length - 1 ? 0 : prev + 1))}
              className="absolute right-6 text-white/85 hover:text-white transition-all bg-white/10 hover:bg-white/20 p-4 rounded-full hover:scale-105 active:scale-95 duration-200"
              aria-label="Sonraki Fotoğraf"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Album Title and Photo Counter */}
          <div className="absolute bottom-8 text-center text-white space-y-2">
            <h4 className="text-xl font-serif font-bold tracking-wide">{selectedGallery.title}</h4>
            <p className="text-xs text-gray-400 tracking-wider">
              {lightboxIndex + 1} / {selectedGallery.images.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasinKulturSanat;
